/**
 * Generato automaticamente.
 * @framework/archived-submissions-api
 */
import { useQuery } from 'react-query';
import type {
  QueryFunction,
  QueryKey,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  ArchivedSubmissionDocumentalResponseDto,
  ArchivedSubmissionDto,
  ArchivedSubmissionsCountResponseDto,
  ArchivedSubmissionsResponseDto,
  GetArchivedSubmissionsParams,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

export const getArchivedSubmissions = (
  params: GetArchivedSubmissionsParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ArchivedSubmissionsResponseDto>(
    {
      url: `/fwp-archived-submissions-api/archived-submissions`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetArchivedSubmissionsQueryKey = (
  params: GetArchivedSubmissionsParams,
) =>
  [
    `/fwp-archived-submissions-api/archived-submissions`,
    ...(params ? [params] : []),
  ] as const;

export const getGetArchivedSubmissionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getArchivedSubmissions>>,
  TError = ErrorType<unknown>,
>(
  params: GetArchivedSubmissionsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getArchivedSubmissions>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getArchivedSubmissions>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetArchivedSubmissionsQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getArchivedSubmissions>>
  > = ({ signal }) => getArchivedSubmissions(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetArchivedSubmissionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getArchivedSubmissions>>
>;
export type GetArchivedSubmissionsQueryError = ErrorType<unknown>;

export const useGetArchivedSubmissions = <
  TData = Awaited<ReturnType<typeof getArchivedSubmissions>>,
  TError = ErrorType<unknown>,
>(
  params: GetArchivedSubmissionsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getArchivedSubmissions>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetArchivedSubmissionsQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getArchivedSubmissionsCount = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ArchivedSubmissionsCountResponseDto>(
    {
      url: `/fwp-archived-submissions-api/archived-submissions/count`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetArchivedSubmissionsCountQueryKey = () =>
  [`/fwp-archived-submissions-api/archived-submissions/count`] as const;

export const getGetArchivedSubmissionsCountQueryOptions = <
  TData = Awaited<ReturnType<typeof getArchivedSubmissionsCount>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getArchivedSubmissionsCount>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getArchivedSubmissionsCount>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetArchivedSubmissionsCountQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getArchivedSubmissionsCount>>
  > = ({ signal }) => getArchivedSubmissionsCount(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetArchivedSubmissionsCountQueryResult = NonNullable<
  Awaited<ReturnType<typeof getArchivedSubmissionsCount>>
>;
export type GetArchivedSubmissionsCountQueryError = ErrorType<unknown>;

export const useGetArchivedSubmissionsCount = <
  TData = Awaited<ReturnType<typeof getArchivedSubmissionsCount>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getArchivedSubmissionsCount>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetArchivedSubmissionsCountQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getArchivedSubmissionsId = (
  id: number,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ArchivedSubmissionDto>(
    {
      url: `/fwp-archived-submissions-api/archived-submissions/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetArchivedSubmissionsIdQueryKey = (id: number) =>
  [`/fwp-archived-submissions-api/archived-submissions/${id}`] as const;

export const getGetArchivedSubmissionsIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getArchivedSubmissionsId>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getArchivedSubmissionsId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getArchivedSubmissionsId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetArchivedSubmissionsIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getArchivedSubmissionsId>>
  > = ({ signal }) => getArchivedSubmissionsId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetArchivedSubmissionsIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getArchivedSubmissionsId>>
>;
export type GetArchivedSubmissionsIdQueryError = ErrorType<unknown>;

export const useGetArchivedSubmissionsId = <
  TData = Awaited<ReturnType<typeof getArchivedSubmissionsId>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getArchivedSubmissionsId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetArchivedSubmissionsIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const getArchivedSubmissionsIdDossierToken = (
  id: number,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<ArchivedSubmissionDocumentalResponseDto>(
    {
      url: `/fwp-archived-submissions-api/archived-submissions/${id}/dossier-token`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetArchivedSubmissionsIdDossierTokenQueryKey = (id: number) =>
  [
    `/fwp-archived-submissions-api/archived-submissions/${id}/dossier-token`,
  ] as const;

export const getGetArchivedSubmissionsIdDossierTokenQueryOptions = <
  TData = Awaited<ReturnType<typeof getArchivedSubmissionsIdDossierToken>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getArchivedSubmissionsIdDossierToken>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getArchivedSubmissionsIdDossierToken>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetArchivedSubmissionsIdDossierTokenQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getArchivedSubmissionsIdDossierToken>>
  > = ({ signal }) =>
    getArchivedSubmissionsIdDossierToken(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetArchivedSubmissionsIdDossierTokenQueryResult = NonNullable<
  Awaited<ReturnType<typeof getArchivedSubmissionsIdDossierToken>>
>;
export type GetArchivedSubmissionsIdDossierTokenQueryError = ErrorType<unknown>;

export const useGetArchivedSubmissionsIdDossierToken = <
  TData = Awaited<ReturnType<typeof getArchivedSubmissionsIdDossierToken>>,
  TError = ErrorType<unknown>,
>(
  id: number,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getArchivedSubmissionsIdDossierToken>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetArchivedSubmissionsIdDossierTokenQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
